/**
 * Sortable column headers for the drops list grid.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { cn } from '@/lib/utils';

import type { DropsSortDir, DropsSortKey } from '@/features/drops/utils/sort';

interface SortableColProps {
  label: string;
  sortKey: DropsSortKey;
  align?: 'left' | 'right';
  sortBy: DropsSortKey;
  sortDir: DropsSortDir;
  onSort: (key: DropsSortKey) => void;
  className?: string;
}

function SortableCol({
  label,
  sortKey,
  align = 'left',
  sortBy,
  sortDir,
  onSort,
  className,
}: SortableColProps) {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      aria-label={`Sort by ${label}${active ? ` (${sortDir})` : ''}`}
      className={cn(
        'flex min-w-0 items-center rounded px-2 py-1 text-xs font-medium transition-colors hover:bg-muted/50',
        align === 'left' && 'justify-start text-left',
        align === 'right' && 'justify-end text-right',
        active ? 'text-primary font-semibold' : 'text-muted-foreground',
        className,
      )}
    >
      {label}
      {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : null}
    </button>
  );
}

interface DropsListHeaderProps {
  sortBy: DropsSortKey;
  sortDir: DropsSortDir;
  onSort: (key: DropsSortKey) => void;
}

export const DropsListHeader: FC<DropsListHeaderProps> = ({
  sortBy,
  sortDir,
  onSort,
}) => (
  <>
    <div className="flex flex-wrap gap-1 px-4 py-2 bg-muted/20 border-b border-border lg:hidden">
      {(['timing', 'amount', 'country', 'status'] as const).map((key) => (
        <SortableCol
          key={key}
          label={key.charAt(0).toUpperCase() + key.slice(1)}
          sortKey={key}
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
          className="shrink-0"
        />
      ))}
    </div>
    <div className="hidden lg:grid grid-cols-12 gap-3 px-4 py-2 bg-muted/20 border-b border-border">
      <div className="col-span-7">
        <SortableCol label="Drop" sortKey="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      </div>
      <div className="col-span-2">
        <SortableCol label="Country" sortKey="country" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
      </div>
      <div className="col-span-1">
        <SortableCol
          label="Amount"
          sortKey="amount"
          align="right"
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
        />
      </div>
      <div className="col-span-2">
        <SortableCol
          label="Timing"
          sortKey="timing"
          align="right"
          sortBy={sortBy}
          sortDir={sortDir}
          onSort={onSort}
        />
      </div>
    </div>
  </>
);

export default DropsListHeader;
